import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { KyrgyzstanSearchApi } from 'shared/api/kyrgyzstan/search'

import { getBookingErrorMessage } from './get-booking-error-message'
import type { BookingPriceParams } from './get-booking-price-params'

export interface BookingPriceQuote {
  totalPrice: number
  prepaymentPrice: number
  roomsCount: number
  mbonusAvailable: boolean
  mbonusAmount: number
  ratehawkBookHash?: string | null
}

interface UseBookingPriceQuoteProps {
  params: BookingPriceParams | null
  enabled: boolean
  onError?: (errorText: string, retry: () => void) => void
}

const parsePrice = (price: string) => Number.parseFloat(price) || 0

export const useBookingPriceQuote = ({
  params,
  enabled,
  onError,
}: UseBookingPriceQuoteProps) => {
  const { t } = useTranslation()
  const onErrorRef = useRef(onError)
  const paramsRef = useRef(params)
  const [price, setPrice] = useState<BookingPriceQuote | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  const paramsKey = params
    ? [
        params.realty_object_id,
        params.start_date,
        params.nights,
        params.visitors_count,
        params.children_ages?.join(',') ?? '',
        params.ratehawk_book_hash ?? '',
      ].join('|')
    : ''

  useEffect(() => {
    paramsRef.current = params
  }, [params])

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  const retry = useCallback(() => {
    setRetryCount((count) => count + 1)
  }, [])

  useEffect(() => {
    const currentParams = paramsRef.current

    if (!enabled || !currentParams) {
      setPrice(null)
      setError(null)
      setIsLoading(false)

      return undefined
    }

    const abortController = new AbortController()

    const fetchPrice = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const { data } =
          await KyrgyzstanSearchApi.calculatePriceWithAvailability(
            currentParams,
            abortController.signal,
          )

        if (abortController.signal.aborted) return

        setPrice({
          totalPrice: parsePrice(data.data.total_price),
          prepaymentPrice: parsePrice(data.data.prepayment_price),
          roomsCount: data.data.units_count,
          mbonusAvailable: data.data.mbonus_available,
          mbonusAmount: data.data.mbonus_amount,
          ratehawkBookHash: data.data.ratehawk_book_hash ?? null,
        })
      } catch (requestError) {
        if (abortController.signal.aborted) return

        const errorMessage = getBookingErrorMessage(requestError, t)

        setError(errorMessage)
        setPrice(null)
        onErrorRef.current?.(errorMessage, retry)
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    fetchPrice()

    return () => {
      abortController.abort()
    }
  }, [enabled, paramsKey, retry, retryCount, t])

  return {
    price,
    isLoading,
    error,
    retry,
  }
}
